import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { Skeleton, Switch, Tooltip, Pagination } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { Button, Collapse } from '../../../../components/index';
import { useCollapsePanels } from '../../../../hooks';
import { useGetBreakdownsOccurrencesQuery } from '../../../../services/breakdownOccurrenceApi';
import {
  BreakdownOccurrenceType,
  TreeType,
  Stopover as StopoverType,
} from '../../../../types';
import { BreakdownForm } from './breakdownForm';
import { OccurenceForm } from './occurenceForm';
import { BreakdownOccurenceFormHeader } from './styles';

type BreakdownOccurenceFormProps = {
  setIsVisible: (value: boolean) => void;
  selectedStopover: StopoverType;
};

const { Panel } = Collapse;
const collapsePanels = ['breakdowns', 'occurences'];

export function BreakdownOccurenceForm(props: BreakdownOccurenceFormProps) {
  const { selectedStopover, setIsVisible } = props;
  const PAGE_SIZE = 20;

  const [occurrencePage, setOccurrencePage] = useState(1);
  const [occurrenceTotalCount, setOccurrenceTotalCount] = useState(0);

  const [breakdownPage, setBreakdownPage] = useState(1);
  const [breakdownTotalCount, setBreakdownTotalCount] = useState(0);

  const {
    data: occurrencesData,
    isSuccess: isSucessGetOccurrences,
    isFetching: isFetchingOccurrenceData,
  } = useGetBreakdownsOccurrencesQuery({
    stopover_id: selectedStopover.id,
    primary_type: 'OCCURRENCE',
    page: occurrencePage,
    page_size: PAGE_SIZE,
    ordering: '-created_at',
  });

  const {
    data: breakdownsData,
    isSuccess: isSucessGetBreakdowns,
    isFetching: isFetchingBreakdownData,
  } = useGetBreakdownsOccurrencesQuery({
    stopover_id: selectedStopover.id,
    primary_type: 'BREAKDOWN',
    page: breakdownPage,
    page_size: PAGE_SIZE,
    ordering: '-created_at',
  });

  const [equipments, setEquipments] = useState<TreeType[]>([]);
  const [breakdowns, setBreakdowns] = useState<BreakdownOccurrenceType[]>();
  const [occurences, setOccurences] = useState<BreakdownOccurrenceType[]>();

  const { onChangeSwitch, onOpenPanel, openCollapsePanels } =
    useCollapsePanels(collapsePanels);

  useEffect(() => {
    if (isSucessGetOccurrences && !isFetchingOccurrenceData) {
      if (occurrencesData?.count !== undefined) {
        setOccurrenceTotalCount(occurrencesData.count);
      }

      setOccurences((prev) => {
        const newItems =
          prev?.filter((item) => item && item.id === undefined) || [];

        const backendItems = occurrencesData.results.map((occurrence) => ({
          ...occurrence,
          date: occurrence.date ? moment(occurrence.date) : '',
          updated_at: moment(occurrence?.updated_at).format('DD/MM/YYYY HH:mm'),
        }));

        if (newItems.length > 0) {
          return [...newItems, ...backendItems];
        }
        return backendItems;
      });
    }
  }, [isSucessGetOccurrences, isFetchingOccurrenceData, occurrencesData]);

  useEffect(() => {
    if (isSucessGetBreakdowns && !isFetchingBreakdownData) {
      if (breakdownsData?.count !== undefined) {
        setBreakdownTotalCount(breakdownsData.count);
      }

      setBreakdowns((prev) => {
        const newItems =
          prev?.filter((item) => item && item.id === undefined) || [];

        const backendItems = breakdownsData.results.map((breakdown) => ({
          ...breakdown,
          date: breakdown.date ? moment(breakdown.date) : '',
          updated_at: moment(breakdown?.updated_at).format('DD/MM/YYYY HH:mm'),
        }));

        if (newItems.length > 0) {
          return [...newItems, ...backendItems];
        }
        return backendItems;
      });
    }
  }, [isSucessGetBreakdowns, isFetchingBreakdownData, breakdownsData]);

  function onBack() {
    setIsVisible(false);
  }

  const handleBreakdownPageChange = (page: number) => {
    setBreakdownPage(page);
  };

  const handleOccurrencePageChange = (page: number) => {
    setOccurrencePage(page);
  };

  return (
    <>
      <BreakdownOccurenceFormHeader>
        <div className="container-title">
          <Button
            type="text"
            className="button-back"
            icon={
              <ArrowLeftOutlined
                style={{ color: 'var(--green-sea_dark)', fontSize: '20px' }}
              />
            }
            onClick={() => onBack()}
          />
          <div className="titles">
            <span className="title-stopover-code">{selectedStopover.code}</span>
            <span className="title">Avarias e Ocorrências</span>
          </div>
        </div>
        <div className="expand">
          <Switch
            onChange={onChangeSwitch}
            checked={openCollapsePanels.length > 0}
          />
          <span className="switchLabel">Expandir tudo</span>
        </div>
      </BreakdownOccurenceFormHeader>
      <Collapse activeKey={openCollapsePanels} onChange={onOpenPanel}>
        <Panel key="breakdowns" header={`Avarias (${breakdowns?.length || 0})`}>
          <Skeleton loading={isFetchingBreakdownData}>
            <div style={{ paddingBlockEnd: '24px' }}>
              <Tooltip
                title={
                  breakdowns?.some((item) => item.id === undefined)
                    ? 'É necessário salvar o item adicionado para criar um novo'
                    : ''
                }
                placement="right"
              >
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    setBreakdowns((prevBreakdowns) => {
                      if (!prevBreakdowns)
                        return [{} as BreakdownOccurrenceType];

                      const savedItems = prevBreakdowns.filter(
                        (item) => item.id !== undefined
                      );

                      return [
                        {
                          primary_type: 'BREAKDOWN',
                          updated_at: moment().format('DD/MM/YYYY HH:mm'),
                        },
                        ...savedItems,
                      ];
                    });
                  }}
                  icon={<PlusOutlined />}
                  disabled={breakdowns?.some((item) => item.id === undefined)}
                >
                  Nova avaria
                </Button>
              </Tooltip>
            </div>
            {breakdowns?.map((breakdown, index) => (
              <BreakdownForm
                key={index}
                index={index}
                globalIndex={
                  breakdown.id === undefined
                    ? breakdownTotalCount + 1
                    : breakdownTotalCount -
                      ((breakdownPage - 1) * PAGE_SIZE +
                        index -
                        (breakdowns?.filter((b) => b.id === undefined).length ||
                          0))
                }
                breakdownSelected={breakdown}
                dockings={selectedStopover.dockings}
                equipments={equipments}
                setEquipments={setEquipments}
                setBreakdowns={setBreakdowns}
                stopoverId={selectedStopover.id}
              />
            ))}
            {breakdownTotalCount > PAGE_SIZE && (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Pagination
                  current={breakdownPage}
                  onChange={handleBreakdownPageChange}
                  total={breakdownTotalCount}
                  pageSize={PAGE_SIZE}
                  showSizeChanger={false}
                  size="small"
                />
              </div>
            )}
          </Skeleton>
        </Panel>
        <Panel
          key="occurences"
          header={`Ocorrências (${occurences?.length || 0})`}
        >
          <Skeleton loading={isFetchingOccurrenceData}>
            <div style={{ paddingBlockEnd: '24px' }}>
              <Tooltip
                title={
                  occurences?.some((item) => item.id === undefined)
                    ? 'É necessário salvar o item adicionado para criar um novo'
                    : ''
                }
                placement="right"
              >
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    setOccurences((prevOccurence) => {
                      if (!prevOccurence)
                        return [{} as BreakdownOccurrenceType];
                      const savedItems = prevOccurence.filter(
                        (item) => item.id !== undefined
                      );

                      return [
                        {
                          primary_type: 'OCCURRENCE',
                          updated_at: moment().format('DD/MM/YYYY HH:mm'),
                        },
                        ...savedItems,
                      ];
                    });
                  }}
                  icon={<PlusOutlined />}
                  disabled={occurences?.some((item) => item.id === undefined)}
                >
                  Nova ocorrência
                </Button>
              </Tooltip>
            </div>
            {occurences?.map((occurence, index) => (
              <OccurenceForm
                key={index}
                index={index}
                globalIndex={
                  occurence.id === undefined
                    ? occurrenceTotalCount + 1
                    : occurrenceTotalCount -
                      ((occurrencePage - 1) * PAGE_SIZE +
                        index -
                        (occurences?.filter((o) => o.id === undefined).length ||
                          0))
                }
                occurenceSelected={occurence}
                dockings={selectedStopover.dockings}
                setOccurences={setOccurences}
                stopoverId={selectedStopover.id}
              />
            ))}
            {occurrenceTotalCount > PAGE_SIZE && (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Pagination
                  current={occurrencePage}
                  onChange={handleOccurrencePageChange}
                  total={occurrenceTotalCount}
                  pageSize={PAGE_SIZE}
                  showSizeChanger={false}
                  size="small"
                />
              </div>
            )}
          </Skeleton>
        </Panel>
      </Collapse>
    </>
  );
}
